import { useEffect, useState } from "react";
import { EntityManager } from "@/components/EntityManager";
import { ENTITY_CONFIG } from "@/lib/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RefreshCw, Loader2, GitMerge, CheckCircle2, Undo2 } from "lucide-react";

const ELEVATED = ["super admin", "admin", "supervision"];

function MergeDialog({ open, onOpenChange, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [names, setNames] = useState({});
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState([]);
  const [undoing, setUndoing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [grp, hist] = await Promise.all([
        api.get("/vendors/duplicate-groups"),
        api.get("/vendors/merge-history"),
      ]);
      const gs = (grp.data.groups || []).map((g) => ({ ...g, key: g.members.map((m) => m.id).sort().join("|") }));
      setGroups(gs);
      const n = {};
      gs.forEach((g) => { n[g.key] = g.suggested_name; });
      setNames(n);
      setHistory(hist.data.merges || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not scan for duplicates");
    } finally {
      setLoading(false);
    }
  };

  // Load whenever the dialog is opened (parent sets open=true directly, so
  // Radix onOpenChange never fires for the open transition).
  useEffect(() => {
    if (open) load();
    else { setGroups([]); setNames({}); setHistory([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onOpen = (v) => onOpenChange(v);

  const undo = async (h) => {
    setUndoing(h.id);
    try {
      const { data } = await api.post(`/vendors/merge/undo/${h.id}`);
      toast.success(`Merge undone — ${data.restored_vendors} vendor(s) restored, ${data.reverted_records} transaction(s) reverted`);
      onMerged && onMerged();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    } finally {
      setUndoing(null);
    }
  };

  const merge = async (g) => {
    const canonical_name = (names[g.key] || "").trim();
    if (!canonical_name) return toast.error("Enter the final vendor name");
    setBusy(g.key);
    try {
      const { data } = await api.post("/vendors/merge", {
        canonical_name,
        vendor_ids: g.members.map((m) => m.id),
      });
      toast.success(`Merged ${g.members.length} into "${data.canonical}" · ${data.reassigned} transaction(s) re-linked`);
      setGroups((prev) => prev.filter((x) => x.key !== g.key));
      onMerged && onMerged();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vendor-merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><GitMerge className="h-5 w-5 text-brand" /> Merge duplicate vendors</DialogTitle>
          <DialogDescription>
            Review each group of similar vendors, set the final name, and merge. Transactions are re-linked and the old names are remembered so future invoices map to the one vendor.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : groups.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="vendor-merge-empty">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-success" />
            No likely duplicate vendors found.
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.key} className="rounded-lg border border-border p-3 space-y-3" data-testid="vendor-merge-group">
                <div className="flex flex-wrap gap-1.5">
                  {g.members.map((m) => (
                    <span key={m.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs" data-testid="vendor-merge-member">
                      {m.name}
                      <span className="text-[10px] text-muted-foreground">({m.txns} txn{m.txns === 1 ? "" : "s"})</span>
                    </span>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <div className="flex-1">
                    <label className="text-[11px] text-muted-foreground">Final vendor name</label>
                    <Input
                      value={names[g.key] ?? ""}
                      onChange={(e) => setNames((p) => ({ ...p, [g.key]: e.target.value }))}
                      className="mt-0.5 h-9"
                      data-testid="vendor-merge-name"
                    />
                  </div>
                  <Button onClick={() => merge(g)} disabled={busy === g.key} className="gap-2 shrink-0" data-testid="vendor-merge-confirm">
                    {busy === g.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
                    Merge {g.members.length}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border" data-testid="vendor-merge-history">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
              <Undo2 className="h-3.5 w-3.5" /> Recent merges — undo within 7 days
            </p>
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="vendor-merge-history-row">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{h.canonical_name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      merged {h.merged} · {h.reassigned} txn re-linked · {h.members.filter(Boolean).join(", ")}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="h-7 gap-1 shrink-0" onClick={() => undo(h)} disabled={undoing === h.id} data-testid="vendor-merge-undo">
                    {undoing === h.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />} Undo
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpen(false)} data-testid="vendor-merge-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Vendors() {
  const { user } = useAuth();
  const canSync = ELEVATED.includes(String(user?.role || "").toLowerCase());
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const refresh = () => setReloadKey((k) => k + 1);

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vendors/sync-from-bills");
      toast.success(
        data.created
          ? `${data.created} vendor(s) added from uploaded bills`
          : "Vendor Master already up to date with bills"
      );
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {canSync && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => setMergeOpen(true)} className="gap-2" data-testid="merge-vendors-btn">
            <GitMerge className="h-4 w-4" /> Merge duplicates
          </Button>
          <Button variant="outline" onClick={sync} disabled={busy} className="gap-2" data-testid="sync-vendors-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync from bills
          </Button>
        </div>
      )}
      <EntityManager key={reloadKey} config={ENTITY_CONFIG.vendors} />
      <MergeDialog open={mergeOpen} onOpenChange={setMergeOpen} onMerged={refresh} />
    </div>
  );
}
